import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { 
  User, Mail, Building, Phone, MapPin, Shield, 
  Save, Eye, EyeOff, AlertCircle, CheckCircle,
  Lock, Key, Search, Grid, List, ChevronDown, 
  ChevronUp, X, Check, Plus, 
  Edit, Trash2, FileDown, Eye as ViewIcon, Lock as LockIcon
} from 'lucide-react';
import userService from 'features/administrativo/services/userService';
import LoadingSpinner from './common/LoadingSpinner';
import PageLayout from './common/PageLayout';

const UserProfile = () => {
  const { user, refreshUserData } = useAuth();
  
  const [formData, setFormData] = useState({
    full_name: '',
    corporate_email: '',
    company: '',
    phone: '',
    city: '',
    password: '',
    confirmPassword: ''
  });
  
  // Fora de `formData` de propósito: nunca é restaurada a partir do usuário, só digitada e limpa.
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeTab, setActiveTab] = useState('perfil');
  
  // Estados para aba de permissões
  const [permissionSearch, setPermissionSearch] = useState('');
  const [permissionFilter, setPermissionFilter] = useState('all');
  const [viewMode, setViewMode] = useState('compact'); // 'compact' ou 'matrix'
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [showOrigin, setShowOrigin] = useState(false);

  // Função para aplicar máscara de telefone
  const applyPhoneMask = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    } else if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    } else {
      return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // Carregar dados do usuário ao montar o componente
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        corporate_email: user.corporate_email || '',
        company: user.company?.name || user.company || '',
phone: user.phone ? applyPhoneMask(user.phone) : '',
        city: user.city || '',
        password: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar máscara para telefone
    if (name === 'phone') {
      const maskedValue = applyPhoneMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: maskedValue
      }));
      if (error) setError('');
      if (success) setSuccess('');
      return;
    }
      
    // Validação básica de entrada
    let processedValue = value;
    
    // Limitar tamanho dos campos
    if (name === 'full_name' && value.length > 255) return;
    if (name === 'corporate_email' && value.length > 255) return;
    if (name === 'company' && value.length > 255) return;
    if (name === 'phone' && value.length > 20) return;
    if (name === 'city' && value.length > 100) return;
    
    // Sanitizar entrada
    if (name === 'full_name' || name === 'company') {
      processedValue = value.replace(/[<>]/g, ''); // Remover caracteres perigosos
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    if (error) setError('');
    if (success) setSuccess('');
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    let hasErrors = false;

    // Validações básicas
    const camposObrigatorios = {
      full_name: formData.full_name?.trim(),
      corporate_email: formData.corporate_email?.trim()
    };

    // Verificar campos obrigatórios
    Object.entries(camposObrigatorios).forEach(([key, value]) => {
      if (!value) {
        errors[key] = 'Este campo é obrigatório';
        hasErrors = true;
      }
    });

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (camposObrigatorios.corporate_email && !emailRegex.test(camposObrigatorios.corporate_email)) {
      errors.corporate_email = 'Formato de email inválido';
      hasErrors = true;
    }

    // Validação de senha se fornecida
    if (formData.password) {
      if (!currentPassword) {
        errors.currentPassword = 'Informe a senha atual';
        hasErrors = true;
      }

      if (formData.password.length < 8) {
        errors.password = 'A senha deve ter pelo menos 8 caracteres';
        hasErrors = true;
      }
      
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'As senhas não coincidem';
        hasErrors = true;
      }
    }

    setFieldErrors(errors);

    if (hasErrors) {
      // Fazer scroll para o primeiro campo com erro
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Focar no campo após o scroll
          setTimeout(() => element.focus(), 500);
        }
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      // Rotas do PRÓPRIO usuário (`/users/me`), e não a de administração: esta tela é de quem
      // está logado, e a rota do administrador exigiria uma permissão que deixaria editar qualquer
      // pessoa. A senha vai primeiro: se a atual não conferir, nada é gravado.
      if (formData.password) {
        await userService.updateMyPassword({
          current_password: currentPassword,
          password: formData.password,
          password_confirmation: formData.confirmPassword,
        });
      }

      // O e-mail é o login e não é enviado: quem altera é o administrador.
      await userService.updateMe({
        full_name: formData.full_name.trim(),
        phone: formData.phone?.trim() || null,
        city: formData.city?.trim() || null,
      });

      // Atualizar contexto de autenticação com dados completos do servidor
      // Isso garante que todas as informações (permissões, empresas, etc.) sejam preservadas
      await refreshUserData();
      
      setSuccess(formData.password ? 'Senha alterada. As outras sessões foram encerradas.' : 'Perfil atualizado com sucesso!');
      setIsEditing(false);
      setCurrentPassword('');
      
      // Limpar campos de senha
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

    } catch (err) {
      console.error('Erro ao atualizar perfil:', err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.errors) {
        const errors = Object.values(err.response.data.errors).flat();
        setError(errors.join(', '));
      } else {
        setError('Erro ao atualizar perfil. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Restaurar dados originais
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        corporate_email: user.corporate_email || '',
        company: user.company?.name || user.company || '',
        phone: user.phone ? applyPhoneMask(user.phone) : '',
        city: user.city || '',
        password: '',
        confirmPassword: ''
      });
    }
    setIsEditing(false);
    setCurrentPassword('');
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  // Funções para processar permissões
  const getActionType = (permissionName) => {
    const name = permissionName.toLowerCase();
    if (name.includes('visualizar') || name.includes('ver') || name.includes('view')) return 'read';
    if (name.includes('criar') || name.includes('create')) return 'create';
    if (name.includes('editar') || name.includes('edit')) return 'edit';
    if (name.includes('excluir') || name.includes('delete')) return 'delete';
    if (name.includes('aprovar') || name.includes('approve')) return 'approve';
    if (name.includes('exportar') || name.includes('export')) return 'export';
    return 'other';
  };

  const getActionColor = (actionType) => {
    const colors = {
      read: 'bg-gray-100 text-gray-700 border-gray-200',
      create: 'bg-blue-100 text-blue-700 border-blue-200',
      edit: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      delete: 'bg-rose-100 text-rose-700 border-rose-200',
      approve: 'bg-amber-100 text-amber-700 border-amber-200',
      export: 'bg-slate-100 text-slate-700 border-slate-200',
      other: 'bg-purple-100 text-purple-700 border-purple-200'
    };
    return colors[actionType] || colors.other;
  };

  const getActionIcon = (actionType) => {
    const icons = {
      read: <ViewIcon className="h-3 w-3" />,
      create: <Plus className="h-3 w-3" />,
      edit: <Edit className="h-3 w-3" />,
      delete: <Trash2 className="h-3 w-3" />,
      approve: <Check className="h-3 w-3" />,
      export: <FileDown className="h-3 w-3" />,
      other: <Shield className="h-3 w-3" />
    };
    return icons[actionType] || icons.other;
  };

  const getActionLabel = (actionType) => {
    const labels = {
      read: 'Ler',
      create: 'Criar',
      edit: 'Editar',
      delete: 'Excluir',
      approve: 'Aprovar',
      export: 'Exportar',
      other: 'Outro'
    };
    return labels[actionType] || 'Outro';
  };

  const processPermissions = () => {
    if (!user?.permissions) return { modules: [], summary: {} };
    
    const modules = {};
    const summary = {
      read: 0, create: 0, edit: 0, delete: 0, approve: 0, export: 0, other: 0
    };

    user.permissions.forEach(permission => {
      const category = permission.category || 'Geral';
      const actionType = getActionType(permission.name);
      
      if (!modules[category]) {
        modules[category] = {
          name: category,
          permissions: [],
          actions: new Set()
        };
      }
      
      modules[category].permissions.push({
        ...permission,
        actionType,
        origin: 'Administrador' // Simulado - seria do backend
      });
      
      modules[category].actions.add(actionType);
      summary[actionType]++;
    });

    return {
      modules: Object.values(modules),
      summary
    };
  };

  const filteredPermissions = () => {
    const { modules } = processPermissions();
    
    return modules.filter(module => {
      // Filtro por busca
      if (permissionSearch) {
        const searchLower = permissionSearch.toLowerCase();
        const moduleMatch = module.name.toLowerCase().includes(searchLower);
        const permissionMatch = module.permissions.some(p => 
          p.display_name?.toLowerCase().includes(searchLower) || 
          p.name.toLowerCase().includes(searchLower)
        );
        if (!moduleMatch && !permissionMatch) return false;
      }
      
      // Filtro por diretoria
      if (permissionFilter !== 'all') {
        const directoryMapping = {
          'financeiro': ['financeiro'],
          'comercial': ['comercial'],
          'operacional': ['operacional', 'noc', 'suporte'],
          'administrativo': ['administrativo', 'config', 'solicitacoes', 'system']
        };
        
        const allowedCategories = directoryMapping[permissionFilter] || [];
        
        // Verificar se o módulo corresponde diretamente à categoria
        const moduleCategoryMatch = allowedCategories.some(category => 
          module.name.toLowerCase() === category.toLowerCase()
        );
        
        // Verificar se alguma permissão do módulo tem categoria correspondente
        const permissionCategoryMatch = module.permissions.some(p => {
          const permissionCategory = (p.category || 'Geral').toLowerCase();
          return allowedCategories.some(category => 
            permissionCategory === category.toLowerCase() ||
            permissionCategory.includes(category.toLowerCase())
          );
        });
        
        // Verificar se o nome da permissão contém alguma das categorias permitidas
        const permissionNameMatch = module.permissions.some(p => 
          allowedCategories.some(category => 
            p.name.toLowerCase().includes(category.toLowerCase())
          )
        );
        
        if (!moduleCategoryMatch && !permissionCategoryMatch && !permissionNameMatch) {
          return false;
        }
      }
      
      return true;
    });
  };

  const toggleModule = (moduleName) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleName)) {
      newExpanded.delete(moduleName);
    } else {
      newExpanded.add(moduleName);
    }
    setExpandedModules(newExpanded);
  };

  const toggleAllModules = () => {
    const { modules } = processPermissions();
    if (expandedModules.size === modules.length) {
      setExpandedModules(new Set());
    } else {
      setExpandedModules(new Set(modules.map(m => m.name)));
    }
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <PageLayout showRefreshButton={false}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header da Página */}
        <div className="mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meu Perfil</h1>
            <p className="text-sm text-gray-600">Gerencie suas informações pessoais</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Sistema de Abas */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('perfil')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'perfil'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="h-4 w-4 inline mr-2" />
                Perfil
              </button>
              <button
                onClick={() => setActiveTab('permissoes')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'permissoes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Shield className="h-4 w-4 inline mr-2" />
                Permissões
              </button>
              <button
                onClick={() => setActiveTab('seguranca')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'seguranca'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Lock className="h-4 w-4 inline mr-2" />
                Segurança
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Alertas */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Erro</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Sucesso</h3>
                  <p className="text-sm text-green-700 mt-1">{success}</p>
                </div>
              </div>
            )}

            {/* Conteúdo das Abas */}
            {activeTab === 'perfil' && (
              <div className="space-y-6">
                {/* Informações do Grupo */}
                {user.group && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Building className="h-5 w-5 text-purple-600" />
                      <div>
                        <h3 className="text-sm font-medium text-purple-800">Grupo</h3>
                        <p className="text-sm text-purple-700 mt-1">
                          {user.group.name}
                        </p>
                        {user.group.description && (
                          <p className="text-xs text-purple-600 mt-1">
                            {user.group.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Formulário de Perfil */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Nome Completo */}
                    <div>
                      <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="h-4 w-4 inline mr-2" />
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          fieldErrors.full_name
                            ? 'border-red-500 bg-red-50'
                            : isEditing 
                              ? 'border-gray-300 bg-white' 
                              : 'border-gray-200 bg-gray-50 text-gray-600'
                        }`}
                        placeholder="Digite seu nome completo"
                      />
                      {fieldErrors.full_name && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.full_name}</p>
                      )}
                    </div>

                    {/* Email Corporativo */}
                    <div>
                      <label htmlFor="corporate_email" className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="h-4 w-4 inline mr-2" />
                        Email Corporativo
                      </label>
                      {/* Somente leitura, como a empresa: o e-mail é o login, e trocá-lo é trocar a
                          identidade da conta. */}
                      <input
                        type="email"
                        id="corporate_email"
                        name="corporate_email"
                        value={formData.corporate_email}
                        readOnly
                        disabled
                        className="w-full px-3 py-2 border rounded-lg border-gray-200 bg-gray-50 text-gray-600"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        O e-mail é o seu login; quem altera é o administrador do sistema
                      </p>
                    </div>

                    {/* Empresa (somente leitura) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Building className="h-4 w-4 inline mr-2" />
                        Empresa
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-600 rounded-lg">
                        {formData.company || 'Não definida'}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        A empresa é gerenciada pelo administrador do sistema
                      </p>
                    </div>

                    {/* Telefone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="h-4 w-4 inline mr-2" />
                        Telefone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          isEditing 
                            ? 'border-gray-300 bg-white' 
                            : 'border-gray-200 bg-gray-50 text-gray-600'
                        }`}
                        placeholder="(11) 99999-9999"
                      />
                    </div>

                    {/* Cidade */}
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-2" />
                        Cidade
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        disabled={!isEditing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          isEditing 
                            ? 'border-gray-300 bg-white' 
                            : 'border-gray-200 bg-gray-50 text-gray-600'
                        }`}
                        placeholder="Sua cidade"
                      />
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <User className="h-4 w-4" />
                        <span>Editar Perfil</span>
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleCancel}
                          disabled={loading}
                          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Salvando...</span>
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4" />
                              <span>Salvar Alterações</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </form>
              </div>
            )}

            {activeTab === 'permissoes' && (
              <div className="space-y-6">
                {/* Barra de Ferramentas */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Busca */}
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Buscar por módulo ou permissão..."
                          value={permissionSearch}
                          onChange={(e) => setPermissionSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {permissionSearch && (
                          <button
                            onClick={() => setPermissionSearch('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Filtros e Controles */}
                    <div className="flex flex-wrap gap-2">
                      {/* Filtro por Diretoria */}
                      <select
                        value={permissionFilter}
                        onChange={(e) => setPermissionFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">Todas as Diretorias</option>
                        <option value="financeiro">Financeiro</option>
                        <option value="comercial">Comercial</option>
                        <option value="operacional">Operacional</option>
                        <option value="administrativo">Administrativo</option>
                      </select>

                      {/* Toggle de Visualização */}
                      <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setViewMode('compact')}
                          className={`px-3 py-2 text-sm flex items-center gap-1 ${
                            viewMode === 'compact' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Grid className="h-4 w-4" />
                          Compacta
                        </button>
                        <button
                          onClick={() => setViewMode('matrix')}
                          className={`px-3 py-2 text-sm flex items-center gap-1 ${
                            viewMode === 'matrix' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <List className="h-4 w-4" />
                          Matriz
                        </button>
                      </div>

                      {/* Expandir/Colapsar Tudo */}
                      <button
                        onClick={toggleAllModules}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                      >
                        {expandedModules.size > 0 ? (
                          <>
                            <ChevronUp className="h-4 w-4" />
                            Colapsar
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4" />
                            Expandir
                          </>
                        )}
                      </button>

                      {/* Mostrar Origem */}
                      <button
                        onClick={() => setShowOrigin(!showOrigin)}
                        className={`px-3 py-2 border rounded-lg flex items-center gap-1 ${
                          showOrigin 
                            ? 'bg-blue-100 border-blue-300 text-blue-700' 
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <Shield className="h-4 w-4" />
                        Origem
                      </button>

                      {/* Limpar Filtros */}
                      {(permissionFilter !== 'all' || permissionSearch) && (
                        <button
                          onClick={() => {
                            setPermissionFilter('all');
                            setPermissionSearch('');
                          }}
                          className="px-3 py-2 border border-red-300 rounded-lg hover:bg-red-50 flex items-center gap-1 text-red-700"
                        >
                          <X className="h-4 w-4" />
                          Limpar Filtros
                        </button>
                      )}

                    </div>
                  </div>
                </div>

                {/* Sumário com Chips */}
                {(() => {
                  const { summary } = processPermissions();
                  const filteredModules = filteredPermissions();
                  const totalPermissions = Object.values(summary).reduce((sum, count) => sum + count, 0);
                  const filteredTotal = filteredModules.reduce((sum, module) => sum + module.permissions.length, 0);
                  
                  if (totalPermissions === 0) {
                    return (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <LockIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma permissão atribuída</h3>
                        <p className="text-gray-600">Entre em contato com o administrador para obter permissões</p>
                      </div>
                    );
                  }

                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Resumo de Permissões
                          </h3>
                          {(permissionFilter !== 'all' || permissionSearch) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Filtrado
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-600">
                          {permissionFilter !== 'all' || permissionSearch ? 
                            `${filteredTotal} de ${totalPermissions} permissão(ões)` : 
                            `${totalPermissions} permissão(ões) total(is)`
                          }
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(summary).map(([actionType, count]) => {
                          if (count === 0) return null;
                          return (
                            <div
                              key={actionType}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium ${getActionColor(actionType)}`}
                            >
                              {getActionIcon(actionType)}
                              {getActionLabel(actionType)}
                              <span className="bg-white bg-opacity-50 px-2 py-0.5 rounded-full text-xs">
                                {count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Visualização Compacta */}
                {viewMode === 'compact' && (
                  <div className="space-y-3">
                    {filteredPermissions().map((module) => (
                      <div key={module.name} className="bg-white border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleModule(module.name)}
                          className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {module.permissions.length === 0 ? (
                                <LockIcon className="h-5 w-5 text-gray-400" />
                              ) : (
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              )}
                              <div>
                                <h4 className="font-medium text-gray-900 capitalize">
                                  {module.name.replace('_', ' ')}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {module.permissions.length} permissão(ões)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Chips de ações */}
                              <div className="flex gap-1">
                                {Array.from(module.actions).map(actionType => {
                                  const count = module.permissions.filter(p => p.actionType === actionType).length;
                                  return (
                                    <span
                                      key={actionType}
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(actionType)}`}
                                    >
                                      {getActionIcon(actionType)}
                                      {count}
                                    </span>
                                  );
                                })}
                              </div>
                              {expandedModules.has(module.name) ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </button>
                        
                        {expandedModules.has(module.name) && (
                          <div className="px-4 pb-4 border-t border-gray-100">
                            <div className="pt-3 space-y-2">
                              {module.permissions.map((permission, index) => (
                                <div key={permission.id || index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(permission.actionType)}`}>
                                      {getActionIcon(permission.actionType)}
                                      {getActionLabel(permission.actionType)}
                                    </span>
                                    <span className="text-sm text-gray-700">
                                      {permission.display_name || permission.name}
                                    </span>
                                  </div>
                                  {showOrigin && (
                                    <span className="text-xs text-gray-500">
                                      via: {permission.origin}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Visualização em Matriz */}
                {viewMode === 'matrix' && (
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Módulo</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Ler</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Criar</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Editar</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Excluir</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Aprovar</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Exportar</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredPermissions().map((module) => (
                            <tr key={module.name} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {module.permissions.length === 0 ? (
                                    <LockIcon className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                  <span className="font-medium text-gray-900 capitalize">
                                    {module.name.replace('_', ' ')}
                                  </span>
                                </div>
                              </td>
                              {['read', 'create', 'edit', 'delete', 'approve', 'export'].map(actionType => {
                                const hasPermission = module.actions.has(actionType);
                                return (
                                  <td key={actionType} className="px-4 py-3 text-center">
                                    {hasPermission ? (
                                      <div className="flex justify-center">
                                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(actionType)}`}>
                                          <Check className="h-3 w-3" />
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </div>
            )}

            {activeTab === 'seguranca' && (
              <div className="space-y-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-3 mb-4">
                    <Lock className="h-5 w-5 text-green-600" />
                    <div>
                      <h3 className="text-sm font-medium text-green-800">Alterar Senha</h3>
                      <p className="text-xs text-green-600">
                        Mantenha sua conta segura com uma senha forte
                      </p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Senha atual: sem ela, um computador destravado bastaria para tomar a conta. */}
                    <div className="md:w-1/2 md:pr-2">
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        <Lock className="h-4 w-4 inline mr-2" />
                        Senha Atual
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        autoComplete="current-password"
                        value={currentPassword}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                          fieldErrors.currentPassword ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Sua senha de hoje"
                      />
                      {fieldErrors.currentPassword && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.currentPassword}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nova Senha */}
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                          <Key className="h-4 w-4 inline mr-2" />
                          Nova Senha
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                              fieldErrors.password
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                            }`}
                            placeholder="Mínimo 8 caracteres"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        {fieldErrors.password && (
                          <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                        )}
                      </div>

                      {/* Confirmar Senha */}
                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                          <Key className="h-4 w-4 inline mr-2" />
                          Confirmar Nova Senha
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                              fieldErrors.confirmPassword
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300'
                            }`}
                            placeholder="Confirme a nova senha"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        {fieldErrors.confirmPassword && (
                          <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
                        )}
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex items-center justify-end space-x-4 pt-4 border-t border-green-200">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            password: '',
                            confirmPassword: ''
                          }));
                          setCurrentPassword('');
                          setFieldErrors({});
                        }}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Limpar
                      </button>
                      <button
                        type="submit"
                        disabled={loading || (!formData.password && !formData.confirmPassword)}
                        className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Alterando...</span>
                          </>
                        ) : (
                          <>
                            <Lock className="h-4 w-4" />
                            <span>Alterar Senha</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default UserProfile;


